"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { cn } from "@/lib/utils";
export default function SupportLocalSound() {
  return <section data-section-id="222490" id="newsletter" className="relative dark bg-background text-foreground py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-5 pointer-events-none" style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, var(--primary) 1px, transparent 0)`,
      backgroundSize: '32px 32px'
    }} aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 max-w-3xl">
        <div className="mb-12 text-center">
          <h2 className="font-default font-medium text-4xl md:text-5xl lg:text-6xl tracking-tight mb-4">
            The Baker's Journal
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default">
            Get fresh recipes, artisan tips, and bakery updates delivered straight to your inbox.
          </p>
        </div>

        <FormProvider formKey="newsletter_signup_bakers_journal" formId="newsletter_signup_bakers_journal" sectionName="Support Local Sound" className="border border-border p-8 md:p-12 bg-card/30 backdrop-blur-sm rounded-lg shadow-xl" defaultValues={{
        name: "",
        email: "",
        phone: "",
        support_type: "",
        message: "",
        subscribe_newsletter: false
      }} validationRules={{
        name: {
          required: "Name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          pattern: {
            value: /^[0-9\-\+\s\(\)]*$/,
            message: "Please enter a valid phone number"
          }
        },
        support_type: {
          required: "Please select your interest"
        }
      }} submitText="Join the Crust Club" successMessage="Welcome to the club! Check your inbox for a warm welcome.">
          <SupportForm />
        </FormProvider>
      </div>
    </section>;
}
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const minimalistInputClass = "border-0 border-b border-muted-foreground/30 rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary h-12 text-lg transition-all duration-300 placeholder:text-muted-foreground/40 font-default";
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center space-y-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <h3 className="font-default font-semibold text-2xl tracking-tight text-foreground">You're on the list</h3>
        <p className="text-muted-foreground text-lg font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all font-default">
          Back to Journal
        </Button>
      </div>;
  }
  const supportOptions = ["Weekly Recipes", "Baking Workshops", "New Product Alerts", "Behind the Scenes"];
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">How should we address you?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@crustbakery.com" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">Optional – for order updates</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="support_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Primary Interest</FormLabel>
              <FormControl>
                <select className={cn(minimalistInputClass, "w-full appearance-none cursor-pointer")} {...field}>
                  <option value="" disabled className="bg-card text-muted-foreground">Select an interest</option>
                  {supportOptions.map((opt, i) => <option key={i} data-index={i} value={opt} className="bg-card text-foreground py-2">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Special Requests</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your favorite breads or dietary needs..." className={cn(minimalistInputClass, "w-full min-h-[100px] py-3 resize-y")} {...field} />
            </FormControl>
            <FormDescription className="text-xs font-default">Optional – we love hearing from our community</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subscribe_newsletter" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 appearance-none border border-primary rounded-sm bg-transparent checked:bg-primary checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22white%22%20stroke-width%3D%223%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%2220%206%209%2017%204%2012%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-center bg-no-repeat transition-all cursor-pointer" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer text-base font-default font-medium">
                Join the Crust Club
              </FormLabel>
              <FormDescription className="font-default">
                Receive our monthly digest of recipes and seasonal specials
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-full text-lg font-default font-semibold transition-all duration-300 bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 mt-8">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : 'Join the Crust Club'}
      </Button>
    </Form>;
}